import axiosServices from 'utils/axios';
import type { AxiosResponse } from 'axios';

/**
 * Shipment endpoints consumed by more than one feature (shipments, quotes, drafts,
 * dashboard). Split out of the old src/api/shipment.js during the features/shipments
 * migration (NEX-1385) specifically because these functions have real callers outside
 * `features/shipments/` that haven't migrated yet — `src/shared/` is importable by any
 * feature and by legacy code alike, unlike a feature's own api.ts (see AGENTS.md §2).
 * Shipments-only endpoints stayed in features/shipments/api.ts instead of moving here.
 */

export const getMyShipments = (): Promise<AxiosResponse<unknown>> => axiosServices.get('/shipments/my-shipments');

export const getShipmentById = (id: number | string): Promise<AxiosResponse<unknown>> => axiosServices.get(`/shipments/${id}`);

export const cancelShipment = (id: number | string, reason: string): Promise<AxiosResponse<unknown>> =>
  axiosServices.patch(`/shipments/${id}/cancel`, { reason });

export const confirmPayment = (id: number | string): Promise<AxiosResponse<unknown>> => axiosServices.post(`/shipments/${id}/confirm-payment`);

/** Confirms a finished passenger service so it can move from DELIVERED to COMPLETED. */
export const confirmReceipt = (id: number | string): Promise<AxiosResponse<unknown>> =>
  axiosServices.patch(`/shipments/${id}/confirm-receipt`);

export const listDrafts = (): Promise<AxiosResponse<unknown>> => axiosServices.get('/shipments/drafts');

export const deleteDraft = (id: number | string): Promise<AxiosResponse<unknown>> => axiosServices.delete(`/shipments/drafts/${id}`);

export const createComplaint = (shipmentId: number | string, description: string): Promise<AxiosResponse<unknown>> =>
  axiosServices.post(`/shipments/${shipmentId}/complaints`, { description });

/**
 * Preview of the cancellation penalty before the user confirms. Used by
 * shared/components/CancelShipmentDialog.tsx, which is itself shared between features/shipments
 * (ShipmentsListPage) and features/quotes (the quote-detail page) — moved here during the
 * features/quotes migration (NEX-1385) for the same reason as the rest of this file.
 */
export const getCancellationPreview = (id: number | string): Promise<AxiosResponse<unknown>> =>
  axiosServices.get(`/shipments/${id}/cancel-preview`);

/**
 * Signed URL for a single shipment image. Used by shared/hooks/useSignedImageUrls.ts, itself
 * shared between shared/components/ShipmentPhotosDialog.tsx and ShipmentDetailPanel.tsx.
 */
export const getShipmentImageUrl = (imageId: number | string): Promise<AxiosResponse<unknown>> =>
  axiosServices.get(`/shipments/images/${imageId}/url`);
